import { CARD_WIDTH, Game } from '@phaser/scenes/Game';
import { playAbility } from '@components/sound/Sound';
import { defaultText } from '@phaser/helpers/Text';
import { DEPTH } from '@phaser/data/depth';
import { useLobbyStore } from '@stores';
import * as Shared from '@shared/lib';

type PlayerInParty = {
  drawnEffects?: {
    amountText?: Phaser.GameObjects.Text;
    image: Phaser.GameObjects.Image;
    effect: number;
    amount: number;
  }[];
  healthbar?: Phaser.GameObjects.Rectangle;
  shieldbar?: Phaser.GameObjects.Rectangle;
  deathImage?: Phaser.GameObjects.Image;
  bgBar?: Phaser.GameObjects.Rectangle;
  username?: Phaser.GameObjects.Text;
  message: Shared.PlayerSyncMessage;
  startX?: number;
  endX?: number;
};

export class PartySystem {
  private _scene = Game.current;
  private _barLength = Math.max(CARD_WIDTH / 2, 72);
  private _party: Map<string, PlayerInParty> = new Map();

  public onPlayerSync(message: Shared.PlayerSyncMessage) {
    const newMessage = this._party.has(message.id)
      ? { ...this._party.get(message.id), message }
      : { message };

    this._party.set(message.id, newMessage);
    this._redraw();
  }

  public getPlayerFromPos(x: number, y: number) {
    if (y > 24) return;

    const id = [...this._party.values()]
      .find(p => {
        if (!p.startX || !p.endX) return false;
        return p.startX <= x && p.endX >= x;
      })?.message.id;
    if (!id) return;

    return useLobbyStore.getState().state?.players
      .find(p => p.id.startsWith(id))?.id;
  }

  public playerLeft(playerId: string) {
    const key = [...this._party.keys()].find(k => playerId.startsWith(k));
    if (!key) return;

    const stuff = this._party.get(key)!;

    stuff.bgBar?.destroy();
    stuff.healthbar?.destroy();
    stuff.shieldbar?.destroy();
    stuff.deathImage?.destroy();
    stuff.username?.destroy();
    stuff.drawnEffects?.forEach(e => {
      e.image.destroy();
      e.amountText?.destroy();
    });

    this._redraw();
  }

  public onAbilityUse(data: Shared.AbilityUseMessage) {
    const camera = this._scene.cameras.main;
    const width = camera.displayWidth;
    const height = camera.displayHeight;

    const isSelf = this._scene.id.startsWith(data.id) && data.id.length === 6;
    const ability = Shared.abilityData.find(a => a.id === data.abilityCode);
    if (!ability || !ability.ascentian) return;

    const startX = [...this._party.values()]
      .find(p => data.id.startsWith(p.message.id))?.startX ?? 0;
    const xPos = isSelf ? width / 2 : startX + this._barLength / 2;
    const yPos = isSelf ? height / 2 : 14;
    const scale = isSelf ? 0.5 : 0.1;
    const fontSize = isSelf ? '64px' : '24px';

    const glow = this._scene.add.image(xPos, yPos, 'sprites', 'glow')
      .setScale(scale)
      .setAlpha(0.2)
      .setTint(0xDEF141)
      .setDepth(DEPTH.OWN_EFFECT);

    const text = this._scene.add.text(xPos, yPos, ability.ascentian, {
        ...defaultText,
        fontFamily: 'Ascentian',
        color: '#DEF141',
        fontSize,
      })
      .setOrigin(0.5)
      .setDepth(DEPTH.OWN_EFFECT);

    this._scene.add.tween({
      targets: [glow, text],
      scale: '*=1.2',
      alpha: 0,
      ease: 'Power2',
      duration: 750,
      onComplete: () => {
        text.destroy();
        glow.destroy();
      }
    });

    if (isSelf) playAbility();
  }

  private _redraw() {
    const yPos = 4;

    const { state } = useLobbyStore.getState();
    if (!state) return;

    if (!this._scene.grid.length) {
      return setTimeout(() => this._redraw(), 250);
    }

    for (const [id, player] of this._party) {
      if (this._scene.id.startsWith(id)) continue;

      if (!player.startX || !player.endX) {
        const keys = [...this._party.keys()].filter(k => !this._scene.id.startsWith(k));
        const offset = Array.from(keys).indexOf(id);

        player.startX = this._scene.grid[offset].x - CARD_WIDTH / 2;
        player.endX = player.startX + CARD_WIDTH;
      }

      if (!player.username) {
        const username = state.players.find(p => p.id.startsWith(id))?.username ?? '???';
        player.username = this._scene.add.text(player.startX, yPos, username, {
          ...defaultText,
          strokeThickness: 0,
          fontSize: '12px',
        });
      }

      if (player.message.respawnCountdown) {
        if (player.deathImage) return;

        player.deathImage = this._scene.add.image(player.startX - 2, yPos + 16, 'sprites', 'death')
          .setScale(0.05)
          .setOrigin(0);

        player.shieldbar?.destroy();
        player.healthbar?.destroy();
        player.bgBar?.destroy();
        player.drawnEffects?.forEach(e => {
          e.image.destroy();
          e.amountText?.destroy();
        });

        player.shieldbar = undefined;
        player.healthbar = undefined;
        player.bgBar = undefined;
        player.drawnEffects = [];

        return;
      }

      if (player.deathImage) {
        player.deathImage.destroy();
        player.deathImage = undefined;
      }

      if (!player.bgBar) {
        player.bgBar = this._scene.add.rectangle(player.startX, yPos + 16, this._barLength, 4, 0x333333)
          .setOrigin(0);
      }

      if (!player.shieldbar) {
        player.shieldbar = this._scene.add.rectangle(player.startX + this._barLength / 2, yPos + 16, 0, 4, 0x00DDFF)
          .setOrigin(1, 0);
      }

      if (!player.healthbar) {
        player.healthbar = this._scene.add.rectangle(player.startX + this._barLength, yPos + 16, 0, 4, 0xFF0000)
          .setOrigin(1, 0);
      }

      const shieldSize = this._barLength / 2 * player.message.shield / player.message.maxShield;
      player.shieldbar.setSize(shieldSize, 4);

      const healthSize = this._barLength / 2 * player.message.health / player.message.maxHealth;
      player.healthbar.setSize(healthSize, 4);

      if (!player.drawnEffects) {
        player.drawnEffects = [];
      }

      this._updateEffects(player, yPos + 24);
    }

    const selfPlayer = [...this._party.values()]
      .find(p => this._scene.id.startsWith(p.message.id));
    if (!selfPlayer) return;

    this._updateEffects(selfPlayer, 0, true);
  }

  private _updateEffects(player: PlayerInParty, yPos: number, isSelf = false) {
    if (!player.drawnEffects) {
      player.drawnEffects = [];
    }

    const startX = isSelf ? 0 : (player.startX ?? 0);
    const size = isSelf ? 26 : 18;
    const fontSize = isSelf ? '16px' : '10px';
    const hSize = size / 2;

    for (const effect of player.message.effects) {
      const obj = player.drawnEffects?.find(e => e.effect === effect.effectCode);

      if (!obj) {
        const xPos = startX + (player.drawnEffects?.length ?? 0) * size;
        const data = Shared.effectData.find(e => e.id === effect.effectCode)!;

        const image = this._scene.add.image(xPos, yPos, 'sprites', `${data.key}Effect`)
          .setOrigin(0)
          .setScale(size / 128)
          .setDepth(DEPTH.OWN_EFFECT);

        return player.drawnEffects.push({
          image,
          effect: effect.effectCode,
          amount: 1,
        });
      }

      obj.amount = player.message.effects.filter(e => e.effectCode === effect.effectCode).length;

      if (obj.amount === 1) {
        obj.amountText?.destroy();
        obj.amountText = undefined;
        continue;
      }

      if (obj.amountText) return obj.amountText.setText(obj.amount.toString());

      obj.amountText = this._scene.add.text(obj.image.x + hSize, obj.image.y + hSize, obj.amount.toString(), {
        ...defaultText,
        fontFamily: 'Kode Mono Variable',
        strokeThickness: 0,
        fontSize,
      })
      .setOrigin(0.5)
      .setDepth(DEPTH.OWN_EFFECT);
    }

    let hasDeletedEffect = false;
    for (const effect of player.drawnEffects) {
      if (!player.message.effects.find(e => e.effectCode === effect.effect)) {
        hasDeletedEffect = true;
        effect.image.destroy();
        effect.amountText?.destroy();
        player.drawnEffects = player.drawnEffects.filter(e => e.effect !== effect.effect);
      }
    }

    if (hasDeletedEffect) {
      player.drawnEffects.forEach((effect, i) => {
        effect.image.x = startX + i * size;
        effect.amountText?.setPosition(effect.image.x + hSize, effect.image.y + hSize);
      });
    }
  }
}